import styled from "@emotion/styled";
import { useState } from "react";
import { translate, postKmsData } from "@kaltura/mediaspace-shared-utils";
import { Switch } from "@kaltura/ds-react-components";
import { Container } from "@kaltura/mediaspace-shared-ui";
import { FormLabel } from "@kaltura/mediaspace-shared-ui/form";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";

interface InlineToggleFieldProps {
    categoryId: string;
    value: boolean;
    label: string;
    url: string;
    onChange?: (checked: boolean) => void;
    onError?: () => void;
}

const InlineContainer = styled(Container)(({ theme }) => ({
    marginTop: 0,
    flexDirection: "row",
    columnGap: theme.spacing(1),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        display: "flex", // override original
    },
}));

const StyledFormInlineLabel = styled(FormLabel)(({ theme }) => ({
    height: theme.spacing(2),
    position: "relative",
    bottom: 1,
}));

/**
 * inline toggle connected to kms
 */
export const InlineToggleField = (props: InlineToggleFieldProps) => {
    const { categoryId, value, label, url, onChange, onError } = props;

    const [checked, setChecked] = useState(value);
    const [processing, setProcessing] = useState(false);

    const { showToast } = useToastsContext();

    // toggle value
    const handleToggle = async (checked: boolean) => {
        // set the user requested value
        setChecked(checked);
        onChange?.(checked);
        setProcessing(true);

        try {
            const { success, active } = await postKmsData(url, { categoryId: categoryId, checked: checked });

            // set the real value
            success ? setChecked(active) : setChecked(!checked);
            success ? onChange?.(active) : onChange?.(!checked);

            // report success
            success && showToast({ severity: "success", message: translate("Settings were saved successfully") });

            // report errors
            !success && onError?.();
            !success && showToast({ severity: "error", message: translate("Error while saving changes") });
        }
        catch (e) {
            setChecked(!checked);
            onChange?.(!checked);
            onError?.();
            showToast({ severity: "error", message: translate("Error while saving changes") });
        }

        setProcessing(false);
    };

    return (
        <Container>
            <InlineContainer>
                <Switch
                    id={"toggle-switch"}
                    onChange={(event, checked) => {
                        handleToggle(checked);
                    }}
                    checked={checked}
                    disabled={processing}
                    size={"small"}
                    inputProps={{ "aria-describedby": "toggle-switch-description" }}
                />
                <StyledFormInlineLabel
                    fontWeightBold={true}
                    id={"toggle-switch-description"}
                    htmlFor={"toggle-switch"}
                    text={label}
                />
            </InlineContainer>
        </Container>
    );
};
export default InlineToggleField;
